import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  parseTipsStatementFilter,
  resolveTipsStatementTransactionTypeFilter,
  TIPS_STATEMENT_FILTER_QUERY_PARAM,
  TipsStatementFilter,
} from '../features/tips-statement/model/tips-statement-filter';
import { selectTipsStatementSections } from '../features/tips-statement/model/tips-statement-records';
import { TipsStatementFilterTabs } from '../features/tips-statement/ui/tips-statement-filter-tabs';
import { TipsStatementHeader } from '../features/tips-statement/ui/tips-statement-header';
import { TipsStatementSectionList } from '../features/tips-statement/ui/tips-statement-section-list';
import {
  type EmployeeProfile,
  getEmployeeTransactions,
} from '../shared/api/employee.service';
import { useEmployeeSession } from '../shared/hooks/use-employee-session';
import { resolveEmployeeSessionCountryCode } from '../shared/model';
import {
  BaseButton,
  BottomNavPageLayout,
  HomeDashboardBottomNav,
} from '../shared/ui';

const FRAME_TOP_PADDING_PX = 20;
const FRAME_SIDE_PADDING_PX = 29;
const FOOTER_TOP_PADDING_PX = 12;
const FOOTER_BOTTOM_PADDING_PX = 13;
const TRANSACTIONS_PAGE_SIZE = 100;
const TRANSACTIONS_LIMIT_MAX = 1_000;

const ContentLayout = styled.div.attrs({
  'data-ui': 'statement-page-content',
})`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  row-gap: ${({ theme }) => theme.scale.px(12)};
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_TOP_PADDING_PX)}, env(safe-area-inset-top))`};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};

  & > [data-ui='tips-statement-filter-tabs'] {
    justify-self: center;
  }
`;

const ScrollableContent = styled.div.attrs({
  'data-ui': 'statement-page-scroll-content',
})`
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding-top: ${({ theme }) => theme.scale.px(12)};
  padding-right: ${({ theme }) => theme.scale.px(1)};
`;

const StatusContent = styled.div.attrs({
  'data-ui': 'statement-page-status-content',
})`
  min-height: 100%;
  display: grid;
  place-items: center;
  padding: ${({ theme }) => theme.scale.px(24)}
    ${({ theme }) => theme.scale.px(12)};
`;

const StatusCard = styled.div.attrs({
  'data-ui': 'statement-page-status-card',
})`
  width: 100%;
  max-width: ${({ theme }) => theme.scale.px(280)};
  display: grid;
  justify-items: center;
  gap: ${({ theme }) => theme.scale.px(14)};
  text-align: center;
`;

const StatusMessage = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 500;
  line-height: ${({ theme }) => theme.scale.px(22)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatementFeed = styled.div.attrs({
  'data-ui': 'statement-page-feed',
})`
  display: grid;
  align-content: start;
  gap: ${({ theme }) => theme.scale.px(18)};
  padding-bottom: ${({ theme }) => theme.scale.px(24)};
`;

const RetryButton = styled(BaseButton)`
  min-width: ${({ theme }) => theme.scale.px(132)};
  min-height: ${({ theme }) => theme.scale.px(44)};
  padding: ${({ theme }) => theme.scale.px(10)}
    ${({ theme }) => theme.scale.px(18)};
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.brand.primary};
  font-size: ${({ theme }) => theme.scale.px(14)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(18)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const LoadMoreButton = styled(BaseButton).attrs({
  'data-ui': 'statement-page-load-more',
})`
  width: ${({ theme }) => theme.scale.px(223)};
  height: ${({ theme }) => theme.scale.px(35)};
  justify-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.support.infoSubtle};
  font-family: Rubik, sans-serif;
  font-size: ${({ theme }) => theme.scale.px(15)};
  font-weight: 600;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.text.primary};

  &:disabled {
    opacity: 0.6;
  }
`;

const BottomNavSlot = styled.div.attrs({
  'data-ui': 'statement-page-bottom-nav-slot',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  padding-top: ${({ theme }) => theme.scale.px(FOOTER_TOP_PADDING_PX)};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-bottom: ${({ theme }) =>
    `max(${theme.scale.px(
      FOOTER_BOTTOM_PADDING_PX,
    )}, env(safe-area-inset-bottom))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

export function StatementPage() {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [transactionsLimit, setTransactionsLimit] = useState(
    TRANSACTIONS_PAGE_SIZE,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const { authContact } = useEmployeeSession();
  const activeFilter = parseTipsStatementFilter(
    searchParams.get(TIPS_STATEMENT_FILTER_QUERY_PARAM),
  );
  const activeTransactionType =
    resolveTipsStatementTransactionTypeFilter(activeFilter);
  const transactionsQueryFilterKey = activeTransactionType ?? 'all';
  const transactionsQuery = useQuery({
    // Employee.GetTransactions exposes cumulative pagination via limit +
    // hasMoreItems, so "Load more" expands the requested window.
    queryFn: () =>
      getEmployeeTransactions(
        activeTransactionType
          ? {
              limit: transactionsLimit,
              type: activeTransactionType,
            }
          : {
              limit: transactionsLimit,
            },
      ),
    queryKey: [
      'employee',
      'transactions',
      transactionsQueryFilterKey,
      transactionsLimit,
    ],
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === transactionsQueryFilterKey
        ? previousData
        : undefined,
  });
  // The authenticated route already owns a reactive ['employee', 'profile']
  // query before this page renders, so this page can safely read the cache
  // snapshot without mounting a second observer for the same entity.
  const profile =
    queryClient.getQueryData<EmployeeProfile>(['employee', 'profile']) ?? null;
  const countryCode = resolveEmployeeSessionCountryCode(profile, authContact);
  const sections = selectTipsStatementSections({
    countryCode,
    filter: activeFilter,
    language: i18n?.language ?? 'en',
    transactions: transactionsQuery.data?.items ?? [],
  });
  const hasMoreTransactions = transactionsQuery.data?.hasMoreItems ?? false;
  const canLoadMoreTransactions =
    hasMoreTransactions && transactionsLimit < TRANSACTIONS_LIMIT_MAX;
  const isLoadingMoreTransactions =
    transactionsQuery.isFetching && transactionsQuery.data !== undefined;

  const setFilter = (filter: TipsStatementFilter) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    setTransactionsLimit(TRANSACTIONS_PAGE_SIZE);

    if (filter === 'all') {
      nextSearchParams.delete(TIPS_STATEMENT_FILTER_QUERY_PARAM);
    } else {
      nextSearchParams.set(TIPS_STATEMENT_FILTER_QUERY_PARAM, filter);
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const handleLoadMore = () => {
    if (isLoadingMoreTransactions || !canLoadMoreTransactions) {
      return;
    }

    setTransactionsLimit(currentLimit =>
      Math.min(currentLimit + TRANSACTIONS_PAGE_SIZE, TRANSACTIONS_LIMIT_MAX),
    );
  };

  let statementContent = (
    <StatementFeed>
      {sections.length > 0 ? (
        <TipsStatementSectionList sections={sections} />
      ) : null}
      {canLoadMoreTransactions ? (
        <LoadMoreButton
          type="button"
          aria-label={t('tipsStatement.loadMore')}
          aria-busy={isLoadingMoreTransactions}
          disabled={isLoadingMoreTransactions}
          onClick={handleLoadMore}
        >
          {t('tipsStatement.loadMore')}
        </LoadMoreButton>
      ) : null}
    </StatementFeed>
  );

  if (transactionsQuery.isPending) {
    statementContent = (
      <StatusContent>
        <StatusCard>
          <StatusMessage>{t('tipsStatement.loading')}</StatusMessage>
        </StatusCard>
      </StatusContent>
    );
  } else if (transactionsQuery.isError && !transactionsQuery.data) {
    statementContent = (
      <StatusContent>
        <StatusCard>
          <StatusMessage>{t('tipsStatement.error')}</StatusMessage>
          <RetryButton
            type="button"
            aria-label={t('tipsStatement.retry')}
            onClick={() => {
              void transactionsQuery.refetch();
            }}
          >
            {t('tipsStatement.retry')}
          </RetryButton>
        </StatusCard>
      </StatusContent>
    );
  } else if (sections.length === 0 && !canLoadMoreTransactions) {
    statementContent = (
      <StatusContent>
        <StatusCard>
          <StatusMessage>{t('tipsStatement.empty')}</StatusMessage>
        </StatusCard>
      </StatusContent>
    );
  }

  return (
    <BottomNavPageLayout
      frameDataUi="statement-page-frame"
      pageDataUi="statement-page"
      stickyFooter={
        <BottomNavSlot>
          <HomeDashboardBottomNav
            activeTab={null}
            onOpenHome={() => {
              navigate('/home');
            }}
            onOpenMyPage={() => {
              navigate('/my-page');
            }}
          />
        </BottomNavSlot>
      }
    >
      <ContentLayout>
        <TipsStatementHeader
          onBack={() => {
            navigate('/home');
          }}
        />
        <TipsStatementFilterTabs
          activeFilter={activeFilter}
          onChange={setFilter}
        />
        <ScrollableContent>{statementContent}</ScrollableContent>
      </ContentLayout>
    </BottomNavPageLayout>
  );
}
